import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { HealthBehavior, HealthConfig } from '../behaviors/HealthBehavior';
import { LootBehavior, LootDrop } from '../behaviors/LootBehavior';
import { EntityTiming } from '../../constants/enums';

// State types for structures (simpler than Unit)
export type StructureActionState = 'none' | 'attacking' | 'transforming';
export type StructureEffectState = 'none' | 'disabled' | 'empowered';
export type StructureDamageState = 'none' | 'light' | 'medium' | 'heavy' | 'critical';
export type StructureSpecialState = 'none' | 'death';
export type StructurePhaseState = 'start' | 'default' | 'transform' | 'dead';

/**
 * Drop configuration for structures
 * Re-exported from LootBehavior for backwards compatibility
 */
export type StructureDrop = LootDrop;

export interface StructureStates {
  action: StructureActionState;
  effect: StructureEffectState;
  damage: StructureDamageState;
  special: StructureSpecialState;
  phase: StructurePhaseState;
}

// Mapping states to animation names for structures
// Animation names MUST be lowercase per project rules
const STRUCTURE_ANIMATION_NAMES: Record<string, string> = {
  start: 'start',
  default: 'default',
  transform: 'transform',
  dead: 'dead',
  attacking: 'attacking',
  transforming: 'transforming',
  disabled: 'disabled',
  light: 'damagedlight',
  medium: 'damagedmedium',
  heavy: 'damagedheavy',
  critical: 'critical',
  death: 'death'
};

export interface StructureConfig extends EntityConfig {
  hp?: number;
  maxHp?: number;
  /** Animation to play on death (e.g., 'fireloop', 'death'). If not set, stays on last frame */
  deathAnimation?: string;
  /** Drops configuration - loot table system */
  drops?: StructureDrop[];
  behaviors?: {
    health?: HealthConfig;
    [key: string]: unknown;
  };
}

/**
 * Structure - Base class for static entities with HP
 *
 * Used for: Farm1, Turret, destructible buildings, etc.
 *
 * Features:
 * - Has HealthBehavior
 * - Cannot move (immovable, no gravity)
 * - Can shoot
 * - Can have states (Start, Transform, Default, Dead)
 */
export class Structure extends Entity {
  public health: HealthBehavior;

  // Multi-category state system (simpler than Unit)
  public states: StructureStates = {
    action: 'none',
    effect: 'none',
    damage: 'none',
    special: 'none',
    phase: 'default'
  };

  /** Animation to play on death (e.g., 'fireloop'). If null, stays on last frame */
  protected deathAnimation: string | null = null;
  /** Current damage frame (for prorate mode) */
  protected currentDamageFrame: number = 0;
  /** Drop configuration (loot table) */
  protected drops: StructureDrop[] = [];

  // Legacy state property for backwards compatibility
  // Note: Phaser GameObject also has a 'state' property, so we need both getter and setter
  public get state(): string {
    return this.states.phase;
  }

  public set state(value: string | number) {
    // Accept string phase values
    if (typeof value === 'string') {
      const validPhases: StructurePhaseState[] = ['start', 'default', 'transform', 'dead'];
      const lowerValue = value.toLowerCase() as StructurePhaseState;
      if (validPhases.includes(lowerValue)) {
        this.states.phase = lowerValue;
      }
    }
    // Ignore numeric values (Phaser's default state)
  }

  /** LootBehavior instance for handling drops */
  protected lootBehavior: LootBehavior | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: StructureConfig = {}
  ) {
    super(scene, x, y, texture, config);

    // Sync phase state with initialAnimation from config
    // This ensures setDamageFrame() doesn't override the initial frame
    if (config.initialAnimation === 'start') {
      this.states.phase = 'start';
    } else if (config.initialAnimation === 'transform') {
      this.states.phase = 'transform';
    }

    // Store config
    this.deathAnimation = config.deathAnimation ?? null;
    this.drops = config.drops ?? [];

    // Structures have health with prorate support
    // HealthBehavior auto-activates prorate if totalFrames > 1 (from behaviors.health)
    this.health = this.addBehavior(new HealthBehavior(this, {
      hp: config.hp ?? 100,
      maxHp: config.maxHp ?? config.hp ?? 100,
      ...config.behaviors?.health
    }));

    // Add LootBehavior if drops are configured (uses 'random' mode for structures)
    if (this.drops.length > 0) {
      this.lootBehavior = this.addBehavior(new LootBehavior(this, {
        drops: this.drops,
        spawnMode: 'random',
        triggerEvent: 'death',
      }));
    }

    // Structures are static
    if (this.body) {
      (this.body as Phaser.Physics.Arcade.Body).setImmovable(true);
      (this.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    }
  }

  /**
   * Override preUpdate to optionally lock to the initial frame every tick.
   * This prevents unwanted idle animations for structures that should only
   * change frames when damaged.
   */
  override preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);

    const configAny = this.config as StructureConfig & { lockInitialFrame?: boolean };
    if (!configAny.lockInitialFrame) return;

    const isDefaultState =
      this.states.special === 'none' &&
      this.states.effect === 'none' &&
      this.states.action === 'none' &&
      this.states.phase === 'default' &&
      this.states.damage === 'none';

    if (!isDefaultState) return;

    // In prorate mode, allow damage frames once HP drops below max
    if (this.health.useProRate && this.health.hp < this.health.maxHp) {
      return;
    }

    if (this.anims?.isPlaying) {
      this.anims.stop();
    }

    const initialAnim = (configAny.initialAnimation ?? 'default').toString().toLowerCase();
    const initialFrame = configAny.initialFrame ?? 0;
    const baseFrameKey = `${this.texture.key}_${initialAnim}_${initialFrame}`;
    const frameKey = this.findFrameKey(baseFrameKey);
    if (frameKey && this.frame?.name !== frameKey) {
      this.setFrame(frameKey);
      this.currentAnimName = initialAnim;
      this.currentFrameIndex = initialFrame;
    }
  }

  // ========================================
  // State Setters
  // ========================================

  /**
   * Set action state (none, attacking, transforming)
   */
  setAction(state: StructureActionState): void {
    if (this.states.special === 'death') return;
    this.states.action = state;
    this.updateAnimation();
    this.emit('actionChanged', state);
  }

  /**
   * Set effect state (none, disabled, empowered)
   */
  setEffect(state: StructureEffectState): void {
    if (this.states.special === 'death') return;
    this.states.effect = state;
    this.updateAnimation();
    this.emit('effectChanged', state);
  }

  /**
   * Set damage state (none, light, medium, heavy, critical)
   * Called automatically by HealthBehavior in threshold mode
   */
  setDamage(state: StructureDamageState): void {
    if (this.states.special === 'death') return;
    this.states.damage = state;
    this.updateAnimation();
    this.emit('damageChanged', state);
  }

  /**
   * Set damage frame directly (C3 prorate mode)
   * Called automatically by HealthBehavior in prorate mode.
   * Formula: frame = totalFrames * (1 - currentHP/maxHP)
   * @param frame The frame index to display (0 = healthy, higher = more damaged)
   */
  setDamageFrame(frame: number): void {
    if (this.states.special === 'death') return;

    const previousFrame = this.currentDamageFrame;
    this.currentDamageFrame = frame;

    // If we're in a phase animation (start, transform) and taking damage,
    // interrupt the animation and transition to default
    const phase = this.states.phase;
    if (phase === 'start' || phase === 'transform') {
      console.log(`[Structure] Damage interrupts ${phase} animation for ${this.texture.key}, transitioning to default`);
      if (this.anims) {
        this.anims.stop();
      }
      this.states.phase = 'default';
    }

    // Get the frame key for this texture (case-insensitive lookup)
    const textureKey = this.texture.key;
    const baseFrameKey = `${textureKey}_default_${frame}`;
    const frameKey = this.findFrameKey(baseFrameKey);

    // DEBUG: Log frame change
    console.log(`[Structure] setDamageFrame for ${textureKey}:`, {
      previousFrame,
      newFrame: frame,
      baseFrameKey,
      foundFrameKey: frameKey,
      hp: this.health.hp,
      maxHp: this.health.maxHp,
      totalFrames: this.health.totalFrames
    });

    if (frameKey) {
      this.setFrame(frameKey);
      // CRITICAL: Update tracked animation state for imagepoint lookups
      // setFrame() bypasses animation events, so we must manually sync these
      this.currentFrameIndex = frame;
      this.currentAnimName = 'default'; // Damage frames are always from "default" animation
    } else if (!this.scene.textures.exists(textureKey)) {
      console.warn(`[Structure] Texture not found: ${textureKey}`);
    } else {
      console.warn(`[Structure] Frame not found: ${baseFrameKey}`);
    }

    this.emit('damageFrameChanged', frame);

    // Update hitbox for this damage frame
    this.updateHitboxForDamageFrame(frame);
  }

  /**
   * Update physics hitbox to match the current damage frame.
   * Structures use 'default' animation for damage frames in collision-c3data.json.
   * @param frameIndex The damage frame index (0 = healthy, higher = more damaged)
   */
  protected updateHitboxForDamageFrame(frameIndex: number): void {
    if (!this.collisionManager) return;

    const hitbox = this.collisionManager.getFrameHitbox(
      this.texture.key,
      'default',  // Structures use 'default' animation for damage frames
      frameIndex
    );

    if (hitbox) {
      this.updatePhysicsBodyFromHitbox(hitbox);
    }
  }

  /**
   * Set special state (none, death)
   * Death is irreversible
   */
  setSpecial(state: StructureSpecialState): void {
    if (this.states.special === 'death' && state !== 'death') return;
    this.states.special = state;
    this.updateAnimation();
    this.emit('specialChanged', state);
  }

  /**
   * Set phase state (start, default, transform, dead)
   */
  setPhase(state: StructurePhaseState): void {
    if (this.states.special === 'death') return;
    const oldPhase = this.states.phase;
    this.states.phase = state;
    this.updateAnimation();

    // Re-apply damage frame when exiting start/transform phase to default
    // (setDamageFrame was blocked during phase animations)
    const wasInPhaseAnimation = oldPhase === 'start' || oldPhase === 'transform';
    const isNowDefault = state === 'default';

    if (wasInPhaseAnimation && isNowDefault) {
      // Apply current damage frame (works with or without prorate mode)
      const textureKey = this.texture.key;
      const baseFrameKey = `${textureKey}_default_${this.currentDamageFrame}`;
      const frameKey = this.findFrameKey(baseFrameKey);
      if (frameKey) {
        this.setFrame(frameKey);
        // CRITICAL: Update tracked animation state for imagepoint lookups
        this.currentFrameIndex = this.currentDamageFrame;
        this.currentAnimName = 'default';
      }
    }

    this.emit('phaseChanged', state, oldPhase);
    // Also trigger legacy callback
    this.onStateChange(state, oldPhase);
  }

  // ========================================
  // Animation Resolution
  // ========================================

  /**
   * Resolve which animation to play based on current states
   */
  protected selectAnimationByState(): string {
    const { action, effect, damage, special, phase } = this.states;

    // 1. Special states have maximum priority
    if (special === 'death') return 'death';

    // 2. Effect states
    if (effect === 'disabled') return 'disabled';

    // 3. Action states
    if (action === 'transforming') return 'transform';
    if (action === 'attacking') return 'attacking';

    // 4. Try combinations with damage
    if (damage !== 'none') {
      const baseAnim = STRUCTURE_ANIMATION_NAMES[phase] || 'default';
      const combo = baseAnim + damage;  // e.g., 'defaultlight' - lowercase per project rules
      if (this.hasAnimation(combo)) return combo;

      // Try generic damage animation
      const damageAnim = STRUCTURE_ANIMATION_NAMES[damage];
      if (damageAnim && this.hasAnimation(damageAnim)) return damageAnim;
    }

    // 5. Base phase
    return STRUCTURE_ANIMATION_NAMES[phase] || 'default';
  }

  /**
   * Capitalize first letter
   */
  private capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /**
   * Get full animation key with texture prefix
   * Animations are named: {texture}_{AnimationName} (e.g., house2_DamagedLight)
   * Uses case-insensitive search to handle manifest/atlas case mismatches
   */
  protected getAnimationKey(animName: string): string {
    const baseKey = `${this.texture.key}_${animName}`;
    // First try exact match
    if (this.scene.anims.exists(baseKey)) {
      return baseKey;
    }
    // Try case-insensitive search
    const lowerKey = baseKey.toLowerCase();
    const animKeys = this.scene.anims.anims.keys();
    for (const key of animKeys) {
      if (key.toLowerCase() === lowerKey) {
        return key;
      }
    }
    // Return original key if not found (will fail gracefully)
    return baseKey;
  }

  /**
   * Find a frame in the texture using case-insensitive search
   * Handles manifest keys (lowercase) vs atlas frame names (camelCase)
   */
  protected findFrameKey(baseFrameName: string): string | null {
    if (!this.scene || !this.scene.textures) {
      return null;
    }

    const textureKey = this.texture.key;
    if (!this.scene.textures.exists(textureKey)) {
      return null;
    }

    const texture = this.scene.textures.get(textureKey);
    const frameNames = texture.getFrameNames();

    // First try exact match
    const exactFrame = texture.get(baseFrameName);
    if (exactFrame && exactFrame.name !== '__BASE') {
      return exactFrame.name;
    }

    // Case-insensitive search through all frames
    const lowerFrameName = baseFrameName.toLowerCase();
    for (const frameName of frameNames) {
      if (frameName.toLowerCase() === lowerFrameName) {
        return frameName;
      }
    }

    // Fallback: Match by numeric suffix (e.g., "_default_0" or "_0")
    const lastUnderscore = baseFrameName.lastIndexOf('_');
    if (lastUnderscore !== -1) {
      const suffix = baseFrameName.slice(lastUnderscore).toLowerCase();
      for (const frameName of frameNames) {
        if (frameName.toLowerCase().endsWith(suffix)) {
          return frameName;
        }
      }
    }

    return null;
  }

  /**
   * Check if animation exists with texture prefix
   */
  protected hasAnimation(animName: string): boolean {
    const key = this.getAnimationKey(animName);
    return this.scene.anims.exists(key);
  }

  /**
   * Update sprite animation based on current states
   * Note: In prorate mode, damage visualization is handled by setDamageFrame()
   * directly setting frames instead of playing animations.
   */
  protected updateAnimation(): void {
    if (!this.scene || !this.scene.anims || !this.scene.textures) {
      return;
    }

    // Optionally lock to initial frame until damage/effects change state
    const configAny = this.config as StructureConfig & { lockInitialFrame?: boolean };
    if (configAny.lockInitialFrame) {
      const isDefaultState =
        this.states.special === 'none' &&
        this.states.effect === 'none' &&
        this.states.action === 'none' &&
        this.states.phase === 'default' &&
        this.states.damage === 'none';

      if (isDefaultState) {
        if (this.anims) {
          this.anims.stop();
        }
        const initialAnim = (configAny.initialAnimation ?? 'default').toString().toLowerCase();
        const initialFrame = configAny.initialFrame ?? 0;
        const baseFrameKey = `${this.texture.key}_${initialAnim}_${initialFrame}`;
        const frameKey = this.findFrameKey(baseFrameKey);
        if (frameKey) {
          this.setFrame(frameKey);
          this.currentAnimName = initialAnim;
          this.currentFrameIndex = initialFrame;
        }
        return;
      }
    }

    // In prorate mode, only allow phase animations (start, transform) and death
    // Damage animations are handled by setDamageFrame() directly
    if (this.health.useProRate) {
      const phase = this.states.phase;
      const isPhaseAnimation = phase === 'start' || phase === 'transform';
      const isDeathAnimation = this.states.special === 'death';

      if (!isPhaseAnimation && !isDeathAnimation) {
        return;
      }
    }

    const animName = this.selectAnimationByState();
    const animKey = this.getAnimationKey(animName);

    if (this.scene.anims.exists(animKey)) {
      if (this.anims?.currentAnim?.key !== animKey) {
        this.play(animKey);
      }
    } else {
      // LOUD FAIL: Animation should exist for phase states
      console.warn(`[Structure] Animation "${animKey}" not found for ${this.texture.key}`);
    }
  }

  // ========================================
  // State Queries
  // ========================================

  /** Check if structure is dead */
  isDead(): boolean { return this.states.special === 'death'; }

  /** Check if structure is attacking */
  isAttacking(): boolean { return this.states.action === 'attacking'; }

  /** Check if structure is disabled */
  isDisabled(): boolean { return this.states.effect === 'disabled'; }

  /** Get current damage level */
  getDamageLevel(): StructureDamageState { return this.states.damage; }

  /** Get current phase */
  getPhase(): StructurePhaseState { return this.states.phase; }

  // ========================================
  // Reset (for pooling)
  // ========================================

  /**
   * Reset all states to default values
   */
  resetAllStates(): void {
    this.states = {
      action: 'none',
      effect: 'none',
      damage: 'none',
      special: 'none',
      phase: 'default'
    };
  }

  /**
   * Override Entity.reset to include state reset
   */
  override reset(config: Record<string, unknown> = {}): void {
    super.reset(config);
    this.resetAllStates();
    this.updateAnimation();
  }

  // Shortcuts for health
  get hp(): number {
    return this.health.hp;
  }

  get maxHp(): number {
    return this.health.maxHp;
  }

  /**
   * Take damage (shortcut)
   */
  takeDamage(amount: number): boolean {
    return this.health.takeDamage(amount);
  }

  /**
   * Check if alive
   */
  isAlive(): boolean {
    return this.health.isAlive();
  }

  /**
   * Change structure phase state (legacy compatibility)
   * Use setPhase() for new code
   */
  setState(newState: string): void {
    // Map legacy state names to new phase states
    const phaseMap: Record<string, StructurePhaseState> = {
      'start': 'start',
      'default': 'default',
      'transform': 'transform',
      'dead': 'dead'
    };
    const phase = phaseMap[newState.toLowerCase()] || 'default';
    this.setPhase(phase);
  }

  /**
   * Override in subclasses for state change handling
   */
  onStateChange(newState: string, oldState: string): void {
    // Override in subclasses
  }

  /**
   * Override in subclasses for damage effects
   */
  onDamage(amount: number): void {
    this.setTint(0xff0000);
    this.scene.time.delayedCall(EntityTiming.DamageFlashDuration, () => {
      this.clearTint();
    });
  }

  /**
   * Override in subclasses for death
   * C3 behavior: stays on last frame or plays death animation (FireLoop, Death, etc.)
   * Drops are handled by LootBehavior via 'specialChanged' event
   */
  onDeath(): void {
    this.states.special = 'death';
    this.states.phase = 'dead';

    // Emit death event FIRST so LootBehavior can spawn drops
    this.emit('specialChanged', 'death');
    this.emit('phaseChanged', 'dead', this.states.phase);

    // Stop any current animation
    if (this.anims) {
      this.anims.stop();
    }

    // Check for death animation (e.g., FireLoop for house2)
    if (this.deathAnimation) {
      const deathAnimKey = this.getAnimationKey(this.deathAnimation);
      if (this.scene.anims.exists(deathAnimKey)) {
        this.play(deathAnimKey);
        return;
      }
    }

    // Check for generic death animation
    const deathKey = this.getAnimationKey('death');
    if (this.scene.anims.exists(deathKey)) {
      this.play(deathKey);
      return;
    }

    // No death animation - stay on last damage frame (C3 behavior)
    // The structure is already showing the most damaged frame from prorate
    // Just make sure we're on the last frame and update hitbox
    if (this.health.useProRate && this.health.totalFrames > 0) {
      const lastFrame = this.health.totalFrames - 1;
      const textureKey = this.texture.key;
      const baseFrameKey = `${textureKey}_default_${lastFrame}`;
      const frameKey = this.findFrameKey(baseFrameKey);
      if (frameKey) {
        this.setFrame(frameKey);
        this.updateHitboxForDamageFrame(lastFrame);
      }
    }
  }

  /**
   * Override in subclasses for death handling
   * By default, structures do NOT destroy themselves (C3 behavior)
   * They stay visible as destroyed debris
   */
  handleDeath(): void {
    // Check if entity should be destroyed on death
    if (this.config.destroyOnDeath) {
      // Simple fade out and destroy
      this.scene.tweens.add({
        targets: this,
        alpha: 0,
        duration: 200,
        onComplete: () => {
          this.destroy();
        }
      });
      return;
    }
    // C3 behavior: structures stay visible when destroyed
    // Only destroy if you explicitly want to remove the debris
    // Override in subclass if you need different behavior
  }

  /**
   * Manually destroy the structure (call this if you want to remove debris)
   */
  destroyStructure(delay: number = 0): void {
    if (delay > 0) {
      this.scene.time.delayedCall(delay, () => {
        this.destroy();
      });
    } else {
      this.destroy();
    }
  }
}
